import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, User } from "firebase/app";
import "firebase/analytics";
import "firebase/storage";
import "firebase/firestore";
import "firebase/auth";
import "firebase/functions";
import "firebase/performance";
import { Company, Member, MemberRole, Profile } from "domain-entities";
import { DocumentStore } from "Helpers/StoreHelper";
import { useCompanyStore } from "Components/Company/CompanyStore";
import { useProfileStore } from "Components/Profile/ProfileStore";
import { useTracking } from "../../Providers/TrackingProvider";
import { useApp } from "../../Providers/AppProvider";

export interface Viewer {
  user: User;
  profile: Profile;
  company: Company;
  memberRole?: MemberRole;
  store: {
    profile: DocumentStore<Profile>;
    company: DocumentStore<Company>;
  };
}

interface IAuthContext {
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  viewer?: Viewer;
  error?: Error;
}

interface Props {
  children: JSX.Element;
}

const AuthContext = createContext<IAuthContext>(null as any);

export const AuthProvider = (props: Props) => {
  const {updateViewer} = useTracking();
  const [user, setUser] = useState<User>();
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const profileStore = useProfileStore(user?.uid);
  const companyStore = useCompanyStore(profileStore.item?.company);
  const {token} = useApp();

  useEffect(() => {

    if (token) {
      auth().signInWithCustomToken(token)
          .then(({user}) => {
            setUser(user || undefined);
            setIsLoadingUser(false);
          })
    } else {
      return auth().onAuthStateChanged((nextUser) => {
        setUser(nextUser || undefined);
        setIsLoadingUser(false);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.uid && profileStore.item?.company) {
      updateViewer({
        userId: user.uid,
        companyId: profileStore.item?.company,
        role: getUserRole(companyStore.item?.members, user.uid)
      });
    }
  }, [updateViewer, user?.uid, profileStore.item?.company, companyStore.item?.members]);

  function resetStores() {
    profileStore.reset();
    companyStore.reset();
  }

  function getUserRole(members: { [email: string]: Member } = {}, uid: string): MemberRole | undefined {
    return Object.values(members).find(
        (member) => member.id === uid
    )?.role;
  }

  function getViewer(): Viewer | undefined {
    if (user && profileStore.item && companyStore.item) {
      return {
        user,
        profile: profileStore.item,
        company: companyStore.item,
        memberRole: getUserRole(companyStore.item.members, user.uid),
        store: {
          profile: profileStore,
          company: companyStore,
        },
      };
    }
  }

  function getError() {
    return profileStore.error || companyStore.error;
  }

  function getIsLoading() {
    if (isLoadingUser) {
      return true;
    } else if (user) {
      if (getError()) {
        return false;
      } else {
        return profileStore.isLoading || companyStore.isLoading;
      }
    } else {
      return false;
    }
  }

  const context: IAuthContext = {
    isLoading: getIsLoading(),
    error: getError(),
    viewer: getViewer(),
    signIn: async (email, password) => {
      resetStores();
      await auth().signInWithEmailAndPassword(email, password);
    },
    signOut: async () => {
      await auth().signOut();
      resetStores();
    },
  };

  return (
    <AuthContext.Provider value={context}>
      {props.children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export function useViewer() {
  return useAuth().viewer!;
}
